import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useAuthStore } from '../../stores/authStore';

export default function WalletButton() {
  const { connected, publicKey, disconnect } = useWallet();
  const { setVisible } = useWalletModal();
  const { balance, isCheckingBalance } = useAuthStore();

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  if (!connected) {
    return (
      <button
        onClick={() => setVisible(true)}
        className="group relative flex items-center gap-2 h-11 px-5 rounded-full bg-white/[0.03] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] transition-all duration-300"
      >
        {/* Subtle glow on hover */}
        <div className="absolute inset-0 rounded-full bg-[#00ff88]/0 group-hover:bg-[#00ff88]/5 transition-colors duration-300" />

        {/* Wallet icon */}
        <svg
          className="w-4 h-4 text-white/60 group-hover:text-[#00ff88] transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"
          />
        </svg>

        <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">
          Connect
        </span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* $BG Balance Display */}
      <div className="hidden sm:flex items-center gap-2 h-11 px-4 rounded-full bg-white/[0.03] border border-white/[0.08]">
        {/* Token icon */}
        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#00ff88]/20 to-[#00ff88]/5 border border-[#00ff88]/30 flex items-center justify-center">
          <span className="text-[10px] font-bold text-[#00ff88]">$</span>
        </div>

        {isCheckingBalance ? (
          <div className="w-12 h-3 bg-white/10 rounded animate-pulse" />
        ) : (
          <span className="text-sm font-mono text-white/80">
            {balance !== null ? (
              <>
                <span className="text-[#00ff88]">{formatBalance(balance)}</span>
                <span className="text-white/40 ml-1">BG</span>
              </>
            ) : (
              <span className="text-white/40">--</span>
            )}
          </span>
        )}
      </div>

      {/* Connected Wallet Button */}
      <button
        onClick={() => disconnect()}
        className="group relative flex items-center gap-2 h-11 px-4 rounded-full bg-white/[0.03] border border-white/[0.08] hover:border-red-500/30 hover:bg-red-500/5 transition-all duration-300"
      >
        {/* Connected indicator */}
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]" />
        </span>

        <span className="text-sm font-mono text-white/80 group-hover:text-red-400 transition-colors">
          {truncateAddress(publicKey?.toBase58() || '')}
        </span>

        {/* Disconnect icon on hover */}
        <svg
          className="w-3.5 h-3.5 text-white/40 group-hover:text-red-400 transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
      </button>
    </div>
  );
}

function formatBalance(balance: number): string {
  if (balance >= 1000000) {
    return (balance / 1000000).toFixed(1) + 'M';
  }
  if (balance >= 1000) {
    return (balance / 1000).toFixed(1) + 'K';
  }
  return balance.toLocaleString();
}
